import { useEffect, useMemo, useState } from 'react';
import { db } from '@/lib/db';
import type { Course, MemberWithStats, Round } from '@/lib/db/types';
import { Sheet } from '@/components/shared/Sheet';

type Tab = 'overview' | 'scores' | 'courses';

interface Props {
  profile: MemberWithStats;
  onClose: () => void;
}

export function GhinDetailModal({ profile, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('overview');
  const [rounds, setRounds] = useState<Round[]>([]);
  const [courses, setCourses] = useState<Record<string, Course>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const [rs, cs] = await Promise.all([
          db.rounds.list({ memberId: profile.id, limit: 30 }),
          db.courses.list(),
        ]);
        if (!active) return;
        setRounds(rs.filter((r) => r.gross_total != null));
        setCourses(Object.fromEntries(cs.map((c) => [c.id, c])));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [profile.id]);

  const avgGross = useMemo(() => {
    if (rounds.length === 0) return null;
    return rounds.reduce((s, r) => s + (r.gross_total || 0), 0) / rounds.length;
  }, [rounds]);

  const lowGross = useMemo(() => {
    if (rounds.length === 0) return null;
    return Math.min(...rounds.map((r) => r.gross_total!));
  }, [rounds]);

  const courseStats = useMemo(() => {
    const stats: Record<string, { count: number; best: number; avg: number }> = {};
    rounds.forEach((r) => {
      if (!r.gross_total) return;
      const cur = stats[r.course_id] || { count: 0, best: r.gross_total, avg: 0 };
      cur.count += 1;
      cur.best = Math.min(cur.best, r.gross_total);
      cur.avg += r.gross_total;
      stats[r.course_id] = cur;
    });
    Object.values(stats).forEach((s) => { s.avg = s.avg / s.count; });
    return stats;
  }, [rounds]);

  const handicapTrend = useMemo(() => {
    const withHcp = rounds.filter((r) => r.posted_handicap != null).slice(0, 8).reverse();
    return withHcp.map((r) => ({ date: r.played_at, hcp: r.posted_handicap! }));
  }, [rounds]);

  return (
    <Sheet title="GHIN Detail" onClose={onClose}>
      <div className="p-4 pb-8">
        <p className="mb-4 text-xs text-slate-500">Mock GHIN view from self-reported index and posted rounds. Live GHIN OAuth coming in Phase 3.</p>

        <div className="mb-4 flex gap-1 rounded-lg border border-line bg-paper p-1">
          {(['overview', 'scores', 'courses'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`flex-1 rounded py-2 text-[10px] font-bold uppercase tracking-wide ${tab === t ? 'bg-navy text-gold' : 'text-slate-500'}`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading && <div className="text-sm text-slate-400">Loading…</div>}

        {!loading && tab === 'overview' && (
          <div className="space-y-3">
            <div className="rounded-xl bg-navy p-4 text-cream">
              <div className="text-xs text-gold">HANDICAP INDEX</div>
              <div className="font-serif text-4xl font-bold">{profile.handicap ?? '—'}</div>
              {profile.handicap_updated_at && (
                <div className="mt-1 text-xs text-white/60">
                  Updated {new Date(profile.handicap_updated_at).toLocaleDateString()}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-paper p-3 shadow-sm">
                <div className="text-[10px] font-bold uppercase text-slate-400">Avg gross</div>
                <div className="font-serif text-2xl font-bold text-navy">{avgGross?.toFixed(1) ?? '—'}</div>
              </div>
              <div className="rounded-xl bg-paper p-3 shadow-sm">
                <div className="text-[10px] font-bold uppercase text-slate-400">Low gross</div>
                <div className="font-serif text-2xl font-bold text-navy">{lowGross ?? '—'}</div>
              </div>
            </div>
            {handicapTrend.length > 1 && (
              <div className="rounded-xl border border-line bg-paper p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Index at post time</div>
                <div className="flex items-end gap-1 h-16">
                  {handicapTrend.map((pt, i) => (
                    <div key={i} className="flex flex-1 flex-col items-center gap-1">
                      <div
                        className="w-full rounded-t bg-gold"
                        style={{ height: `${Math.min(100, pt.hcp * 4)}%`, minHeight: 4 }}
                      />
                      <span className="text-[8px] text-slate-400">{pt.hcp.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && tab === 'scores' && (
          <div className="space-y-2">
            {rounds.length === 0 && <div className="text-sm text-slate-400">No posted scores yet.</div>}
            {rounds.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl bg-paper p-3 shadow-sm">
                <div>
                  <div className="text-sm font-semibold text-navy">{courses[r.course_id]?.name || 'Course'}</div>
                  <div className="text-xs text-slate-400">{new Date(r.played_at).toLocaleDateString()}</div>
                </div>
                <div className="font-serif text-xl font-bold text-navy">{r.gross_total}</div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === 'courses' && (
          <div className="space-y-2">
            {Object.keys(courseStats).length === 0 && <div className="text-sm text-slate-400">No course data yet.</div>}
            {Object.entries(courseStats).map(([id, s]) => (
              <div key={id} className="rounded-xl bg-paper p-3 shadow-sm">
                <div className="font-semibold text-navy">{courses[id]?.name || 'Course'}</div>
                <div className="mt-1 flex gap-4 text-xs text-slate-500">
                  <span>{s.count} rounds</span>
                  <span>Best {s.best}</span>
                  <span>Avg {s.avg.toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Sheet>
  );
}
